<template>
  <v-card>
    <v-toolbar flat color="primary">
      <v-toolbar-title class="font-weight-medium">
        Reschedule Tasks
      </v-toolbar-title>
      <v-spacer />
      <v-btn icon @click="$emit('close')">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-toolbar>
    <v-card-text class="pt-6 px-4">
      <v-form ref="form" @submit.prevent="submitHandler">
        <v-row>
          <v-col cols="12" sm="7">
            <CommonDatePicker
              label="Due date"
              :enter-date="date"
              :disabled="false"
              :rules="[rules.required]"
              outlined
              hide-details="auto"
              dense
              @updateDate="date = $event"
            />
          </v-col>
          <v-col cols="12" sm="5">
            <v-select
              v-model="time"
              :menu-props="{ }"
              :items="times"
              item-title="text"
              :rules="[rules.required]"
              label="Time"
            />
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>
    <v-divider />
    <v-card-actions>
      <v-btn class="cancel" variant="text" @click="$emit('close')">Cancel</v-btn>
      <v-spacer />
      <v-btn
        color="primary"
        :loading="loading"
        data-track="task.selection.reschedule"
        @click="submitHandler"
      >
        Reschedule
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { DateTime } from 'luxon';

import { ITextValue, VForm } from '@/types/common';

import CommonDatePicker from '@/components/common-components/pickers/CommonDatePicker.vue';

interface IRescheduleTasksForm {
  date: string;
  time: string;
  rules: {
    required: (v: any) => boolean | string;
  };
}

export default defineComponent({
  name: 'RescheduleTasksForm',

  components: { CommonDatePicker },

  props: {
    loading: Boolean,
  },

  emits: ['close', 'submit'],

  data: (): IRescheduleTasksForm => ({
    date: '',
    time: '',
    rules: {
      required: (value) => !!value || 'This field is required',
    },
  }),

  computed: {
    times (): ITextValue<string>[] {
      const hours: ITextValue<string>[] = [];

      for (let hour = 0; hour < 24; hour++) {
        hours.push(this.getTimeItem({ hour }));
        hours.push(this.getTimeItem({ hour, minute: 15 }));
        hours.push(this.getTimeItem({ hour, minute: 30 }));
        hours.push(this.getTimeItem({ hour, minute: 45 }));
      }

      return hours;
    },
  },

  methods: {
    getTimeItem ({ hour, minute }: { hour: number; minute?: number }): ITextValue<string> {
      return {
        text: DateTime.fromObject({ hour, minute }).toLocaleString(DateTime.TIME_SIMPLE),
        value: DateTime.fromObject({ hour, minute }).toLocaleString(DateTime.TIME_24_SIMPLE),
      };
    },
    async submitHandler () {
      const { valid } = await (this.$refs.form as VForm).validate();
      if (!valid) {
        return;
      }

      const [hours, minutes] = this.time.split(':').map(Number);
      const dueDate = DateTime.fromISO(this.date)
        .set({ hour: hours, minute: minutes, second: 0, millisecond: 0 })
        .toUTC()
        .toISO();

      this.$emit('submit', dueDate);
    },
    reset () {
      this.date = '';
      this.time = '';
      (this.$refs.form as VForm).resetValidation();
    },
  },
});
</script>
